// React
import { useEffect, useState } from "react"

// Components
import { PrintTransaction, TableHeader } from "components"

// Form
import { useFormContext } from "react-hook-form"

// Third-Party Libraries
import moment from "moment"

// Types
import type { FormSalesOrderType } from "../types"

// Utils
import { convertNumber, useCountryById, useHandleAuth, useProvinceById, useRegencyById, useSubdistrictById, useTaxCode } from "utils"
import { useList } from "pages/Setup/Preference/SettingCompany/utils/hooks"
import { useSubdomainSetting } from "pages/Purchase/Transaction/PurchaseOrder/utils"
import { useCustomerDetail, useDropdownProduct } from "../utils/hooks"
import { generatePath } from "pages/Task/utils/functions"

export function Print(): JSX.Element {
  // Form
  const { formState: { defaultValues } } = useFormContext<FormSalesOrderType>()
 
  // Hooks
  const company = useList()
  const country = useCountryById()
  const customer = useCustomerDetail(defaultValues?.customer_id ?? "")
  const product = useDropdownProduct({ location_id: defaultValues?.location_id })
  const province = useProvinceById()
  const regency = useRegencyById()
  const setting = useSubdomainSetting()
  const subdistrict = useSubdistrictById()
  const taxCode = useTaxCode()
  const { userTokenData } = useHandleAuth()
  const [address, setAddress] = useState<string>("")

  // Vars
  const isLoading: boolean = company.isLoading || customer.isLoading || country.isLoading || province.isLoading || subdistrict.isLoading || regency.isLoading
  const detail = defaultValues?.sodetail ?? []
  const freight: number = defaultValues?.freight ?? 0
  const userToken = userTokenData()
  const subtotal: number = detail.reduce((acc, item): number => {
    return acc + (item?.total ?? 0)
  }, 0)
  const tax: number = detail.reduce((acc, item): number => {
    // Vars
    const tax_item = taxCode.data.find((i) => i.value === item?.tax_id?.toString())
    return acc + ((item?.total ?? 0) * (tax_item?.rate ?? 0)) / 100
  }, 0)
  const total = subtotal + freight + tax

  useEffect(() => {
    company.fetchData().then((res) => {
      setAddress(res.address)

      if (res.subdistrict_id) {
        subdistrict.fetchData(res.subdistrict_id)
      }

      if (res.regency_id) {
        regency.fetchData(res.regency_id)
      }

      if (res.province_id) {
        province.fetchData(res.province_id)
      }

      if (res.country_id) {
        country.fetchData(res.country_id)
      }
    })

    // eslint-disable-next-line
  }, [])

  return (
    <PrintTransaction loading={isLoading}>
      <section className="flex flex-col gap-3">
        <section className="grid grid-cols-2 items-stretch gap-3">
          <img
            src={generatePath(setting.dataCompany?.logo)}
            alt="Logo"
            width={200}
            height={50}
          />

          <section>
            <div className="mb-3 text-xl font-bold text-right">Sales Order</div>

            <section className="flex justify-end">
              <table className="w-full max-w-[250px]">
                <tbody>
                  <tr>
                    <td width={10} className="whitespace-nowrap text-right font-bold">Reference :{" "}</td>
                    <td className="whitespace-nowrap text-right">{defaultValues?.trx_code}</td>
                  </tr>

                  <tr>
                    <td width={10} className="whitespace-nowrap text-right font-bold">Date :{" "}</td>
                    <td className="whitespace-nowrap text-right">{defaultValues?.transaction_date ? moment(defaultValues?.transaction_date).format("DD/MM/YYYY") : "-"}</td>
                  </tr>
                </tbody>
              </table>
            </section>
          </section>
        </section>

        <section className="grid grid-cols-2 items-stretch gap-3">
          <section>
            <div className="font-bold">Company Info</div>
            <div className="divider divider-neutral" />
            <div className="font-bold">{userToken?.company_name}</div>
            <div>{address},</div>
            <div>{regency.data?.regency_name},</div>
            <div>{province.data?.province_name},</div>
            <div>Telp: {setting.data?.phone ?? "-"}</div>
            <div>Email: {setting.data?.email ?? "-"}</div>
          </section>

          <section>
            <div className="font-bold">Bill For</div>
            <div className="divider divider-neutral" />
            <div className="font-bold">{defaultValues?.customer_name}</div>
            <div>Telp: {customer.data?.handphone}</div>
          </section>
        </section>

        <table className="table table-bordered">
          <TableHeader>
            <tr className="text-white">
              <th>ITEM CODE</th>
              <th>DESCRIPTION</th>
              <th>ORDER</th>
              <th>RECEIVED</th>
              <th>BACKORDER</th>
              <th>PRICE</th>
              <th>DISCOUNT</th>
              <th>TAX</th>
              <th>TOTAL</th>
            </tr>
          </TableHeader>

          <tbody>
            {defaultValues?.sodetail?.map((item, key) => {
              // Vars
              const qty_order: number = item?.qty_order ?? 0
              const received: number = item?.received ?? 0
              const productData = product.data.find(
                (i) => i.product_id === item?.product_id
              )
              const taxCodeData = taxCode.data.find(
                (i) => i.value === item?.tax_id?.toString()
              )

              return (
                <tr key={key}>
                  <td>{productData?.product_barcode}</td>
                  <td>
                    <div>{productData?.product_barcode}</div>
                    <div className="mb-4">{item?.description}</div>
                    <div>COLOUR : {productData?.mr_product_colour_name}</div>
                    <div>STYLE : {productData?.mr_product_style_name}</div>
                    <div>WEIGHT : {productData?.weight}</div>
                    <div>UNIT : {productData?.mr_unit_name}</div>
                  </td>
                  <td className="text-right">{convertNumber(qty_order).intoCurrency}</td>
                  <td className="text-right">{convertNumber(received).intoCurrency}</td>
                  <td className="text-right">{convertNumber(qty_order - received).intoCurrency}</td>
                  <td className="text-right">{convertNumber(item?.price_measure ?? 0).intoCurrency}</td>
                  <td className="text-right">{convertNumber(item?.discount ?? 0).intoCurrency}%</td>
                  <td>{taxCodeData?.label}</td>
                  <td className="text-right">{convertNumber(item?.total ?? 0).intoCurrency}</td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <section className="flex justify-end">
          <table className="table max-w-[400px]">
            <tbody>
              <tr className="border-b">
                <td className="font-bold">Subtotal</td>
                <td className="text-right">{convertNumber(subtotal).intoCurrency}</td>
              </tr>

              <tr className="border-b">
                <td className="font-bold">Freight</td>
                <td className="text-right">{convertNumber(freight).intoCurrency}</td>
              </tr>

              <tr className="border-b">
                <td className="font-bold">Tax</td>
                <td className="text-right">{convertNumber(tax).intoCurrency}</td>
              </tr>

              <tr className="border-b">
                <td className="font-bold">Total Amount</td>
                <td className="text-right">{convertNumber(total).intoCurrency}</td>
              </tr>

              <tr className="border-b">
                <td className="font-bold">Payment</td>
                <td className="text-right">{convertNumber(0).intoCurrency}</td>
              </tr>

              <tr className="border-b">
                <td className="font-bold">Balance</td>
                <td className="text-right">{convertNumber(total).intoCurrency}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="grid grid-cols-2 items-stretch gap-3">
          <section>
            <div className="font-bold">Additional Notes</div>
            <div className="divider divider-neutral" />
            <td>{defaultValues?.memo}</td>
          </section>
        </section>
      </section>
    </PrintTransaction>
  )
}